import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ReservationsManagement.css';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import config from '../config';

const ReservationsManagement = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDate, setShowDate] = useState("today");
  const [activityData, setActivityData] = useState({ sales: 0, cancellations: 0, overbookings: 0 });
  const [rooms, setRooms] = useState([]); // Available rooms
  const [roomType, setRoomType] = useState('');
  const [numPersons, setNumPersons] = useState('');
  const [checkInDate, setCheckInDate] = useState(new Date());
  const [checkOutDate, setCheckOutDate] = useState(new Date());
  const [errorMessage, setErrorMessage] = useState('');
  const [isPopupVisible, setIsPopupVisible] = useState(false); // Controls popup visibility
  const [today, setToday] = useState(new Date().toLocaleDateString()); // Today's date for header
  // Fetch reservations for today or tomorrow
  const fetchReservations = async (dateFilter) => {
    try {
      setLoading(true);
      const dateParam = dateFilter === "today" ? today : new Date(Date.now() + 86400000).toLocaleDateString(); // Tomorrow
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/reserve?date=${dateParam}`);
      setReservations(response.data.reservations);
      setActivityData(response.data.activity);
      setLoading(false);
    } catch (err) {
      setError('Failed to load reservations. Please try again later.');
      setLoading(false);
    }
  };

  // Initial fetch when component mounts
  useEffect(() => {
    fetchReservations("today");
  }, []);

  const handleDateChange = (date) => {
    setShowDate(date);
    fetchReservations(date);
  };
   // Fetch available rooms on mount
   useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/rooms`);
        setRooms(response.data);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setErrorMessage('Failed to fetch rooms.');
      }
    };
    fetchRooms();
  }, []);

  // Room pricing logic
  const getRoomPrice = (roomType, numPersons) => {
    let pricePerNight = 0;
    numPersons = Number(numPersons);
    switch (roomType) {
      case 'family':
        pricePerNight = numPersons >= 4 ? 54 : 0;
        break;
      case 'double':
        pricePerNight = numPersons === 2 ? 35 : numPersons === 1 ? 31.5 : 0;
        break;
      case 'twin':
        pricePerNight = numPersons === 2 ? 37 : numPersons === 1 ? 33.5 : 0;
        break;
      default:
        pricePerNight = 0;
    }
    return pricePerNight;
  };

  const handleCreateReservation = async () => {
    setErrorMessage('');
    if (!roomType || !numPersons || !checkInDate || !checkOutDate) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    const timeDifference = new Date(checkOutDate) - new Date(checkInDate);
    const numNights = timeDifference / (1000 * 3600 * 24);

    if (numNights <= 0) {
      setErrorMessage('Check-out date must be after check-in date');
      return;
    }

    const pricePerNight = getRoomPrice(roomType, numPersons);
    if (pricePerNight === 0) {
      setErrorMessage('Invalid room selection for the number of persons');
      return;
    }

    const totalAmount = pricePerNight * numNights;

    // Send reservation data to backend (admin creating reservation)
    const data = {
      room_type: roomType,
      num_persons: numPersons,
      check_in_date: checkInDate.toISOString().split('T')[0],  // Format the date as YYYY-MM-DD
      check_out_date: checkOutDate.toISOString().split('T')[0], // Format the date as YYYY-MM-DD
      amount: totalAmount * 100 // The total amount for the reservation
    };

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/admin/reserve`, data);
      alert('Reservation successfully created!');
      setIsPopupVisible(false); // Close the popup
    } catch (error) {
      console.error('Error creating reservation:', error);
      setErrorMessage('Failed to create reservation.');
    }
  };

  const closePopup = () => {
    setIsPopupVisible(false); // Close the popup
  };
  return (
    <div className="reservations-container">
      <div className="manage-header">
        <span className="date">{today}</span>
        <button className="create-reservation-button" onClick={() => setIsPopupVisible(true)}>
          Create Reservation
        </button>
      </div>
      {/* Admin Popup for Creating Reservation */}
      {isPopupVisible && (
        <div className="popup">
          <div className="popup-content">
            <h2>Create Reservation</h2>
            <form onSubmit={(e) => e.preventDefault()}>
              <label className="form-label">
                Room Type:
                <select
                  className="form-select"
                  value={roomType}
                  onChange={(e) => setRoomType(e.target.value)}
                  required
                >
                  <option value="">Select Room Type</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.room_type}>
                      {room.room_type === 'family' ? 'Family Room' :
                      room.room_type === 'double' ? 'Double Room' :
                      room.room_type === 'twin' ? 'Twin Room' :
                      room.room_type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="form-label">
                Number of Persons:
                <input
                  className="form-input"
                  type="number"
                  value={numPersons}
                  onChange={(e) => setNumPersons(e.target.value)}
                  required
                  min="1"
                />
              </label>

              <label className="form-label">
                Check-in Date:
                <DatePicker
                  selected={checkInDate}
                  onChange={(date) => setCheckInDate(date)}
                  dateFormat="MMMM d, yyyy"
                  minDate={new Date()}
                  required
                  className="form-datepicker"
                />
              </label>

              <label className="form-label">
                Check-out Date:
                <DatePicker
                  selected={checkOutDate}
                  onChange={(date) => setCheckOutDate(date)}
                  dateFormat="MMMM d, yyyy"
                  minDate={checkInDate}
                  required
                  className="form-datepicker"
                />
              </label>

              {errorMessage && <div className="error-message"><p>{errorMessage}</p></div>}
              <div className='buttons'>
              <button type="button" onClick={handleCreateReservation} className="submit-reservation-button">
                Create Reservation
              </button>

              <button type="button" onClick={closePopup} className="close-popup-button">
                Close
              </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <nav className="nav-tabs">
        <button onClick={() => handleDateChange("arrivals")}>Arrivals</button>
        <button onClick={() => handleDateChange("departures")}>Departures</button>
        <button onClick={() => handleDateChange("stayovers")}>Stayovers</button>
        <button onClick={() => handleDateChange("in-home-guests")}>In-home Guests</button>
      </nav>

      <div className="date-selector">
        <label>
          <input type="radio" name="dateFilter" checked={showDate === "today"} onChange={() => handleDateChange("today")} />
          Today
        </label>
        <label>
          <input type="radio" name="dateFilter" checked={showDate === "tomorrow"} onChange={() => handleDateChange("tomorrow")} />
          Tomorrow
        </label>
      </div>

      <div className="summary-boxes">
        <div className="summary-box">Arrivals: {activityData.arrivals}</div>
        <div className="summary-box">Departures: {activityData.departures}</div>
        <div className="summary-box">Accommodations Booked: {activityData.booked}</div>
      </div>

      <div className="content-boxes">
        <div className="box">
          <h3>Reservations</h3>
          <div className='box-line'>
          <p>Summary:</p>
          </div>
          {loading ? <p>Loading reservations...</p> : (
            <table>
              <thead>
                <tr>
                  <th>Guest Name</th>
                  <th>Reference</th>
                  <th>Room Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((reservation) => (
                  <tr key={reservation.id}>
                    <td>{reservation.customer_name}</td>
                    <td>{reservation.reference}</td>
                    <td>{reservation.room_type}</td>
                    <td>{reservation.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="box">
          <h3>Today's Activity</h3>
          <div className='box-liner'>
          <p>Sales: £{activityData.sales}</p>
          <p>Cancellations: {activityData.cancellations}</p>
          <p>Overbookings: {activityData.overbookings}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Guest Name</th>
                <th>Revenue</th>
                <th>Nights</th>
                <th>Check-in Date</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td>{reservation.customer_name}</td>
                  <td>£{reservation.total_amount}</td>
                  <td>{(new Date(reservation.check_out_date) - new Date(reservation.check_in_date)) / (1000 * 3600 * 24)}</td>
                  <td>{reservation.check_in_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReservationsManagement;
